import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useHistory, Link } from 'react-router-dom'
import { Paper, Button } from '@material-ui/core'
import { signIn, confirmSignIn, fetchAuthSession } from '@aws-amplify/auth'
import { Flex, SecondaryHeader } from '../styles/global'
import { Input } from '../components/Input'

import { AUTH_USER_TOKEN_KEY, checkToken } from '../utils/auth.utils'

interface UserCredentials {
  username: string
  password: string
  // if reset is needed
  password1: string
  password2: string
}

export const Login = () => {
  const [formData, setFormData] = useState<UserCredentials>({
    username: '', password: '', password1: '', password2: '',
  })
  const [formTouched, setFormTouched] = useState<{ username: boolean; password: boolean; password1: boolean; password2: boolean }>({
    username: false, password: false, password1: false, password2: false,
  })

  const [isLoading, setLoading] = useState(false)
  const [newPasswordRequired, newChangePasswordRequired] = useState(false)

  const history = useHistory()
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (checkToken()) {
      history.push('/')
    } else {
      // Check if there is an existing Amplify session even if localStorage is empty
      fetchAuthSession().then((session) => {
        if (session.tokens?.idToken) {
          localStorage.setItem(AUTH_USER_TOKEN_KEY, session.tokens.idToken.toString() || '')
          history.push('/')
        }
      }).catch(() => {
        // No session, stay on login page
      })
    }
  }, [history])

  const isLoginValid = formData && formData.password !== '' && formData.username !== ''
  const isPasswordResetValid = formData && formData.password1 !== '' && formData.password2 !== '' && formData.password1 === formData.password2

  const sendCredentials = () => {
    setLoading(true)
    signIn({ username: formData.username, password: formData.password })
      .then(async (result) => {
        if (result.isSignedIn) {
          const { tokens } = result.nextStep.signInStep === 'DONE'
            ? await fetchAuthSession()
            : { tokens: null };

          if (tokens?.idToken) {
            localStorage.setItem(AUTH_USER_TOKEN_KEY, tokens.idToken.toString() || '')
            history.push('/')
          } else {
            if (isMounted.current) {
              newChangePasswordRequired(true)
            }
          }
        } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          if (isMounted.current) {
            newChangePasswordRequired(true)
          }
        }
        if (isMounted.current) {
          setLoading(false)
        }
      })
      .catch(async (err) => {
        if (err.name === 'UserAlreadyAuthenticatedException') {
          try {
            const { tokens } = await fetchAuthSession();
            if (tokens?.idToken) {
              localStorage.setItem(AUTH_USER_TOKEN_KEY, tokens.idToken.toString() || '')
              history.push('/')
              return
            }
          } catch (sessionErr) {
            console.error('Failed to fetch session after UserAlreadyAuthenticatedException', sessionErr)
          }
        }
        if (isMounted.current) {
          setLoading(false)
          localStorage.removeItem(AUTH_USER_TOKEN_KEY)
          console.error(err)
          alert(err.message)
        }
      })
  }

  const resetPassword = () => {
    setLoading(true)
    confirmSignIn({ challengeResponse: formData.password1 })
      .then(async (result) => {
        if (result.isSignedIn) {
          const { tokens } = await fetchAuthSession()
          if (tokens?.idToken) {
            localStorage.setItem(AUTH_USER_TOKEN_KEY, tokens.idToken.toString() || '')
            setTimeout(() => {
              if (isMounted.current) {
                history.push('/')
              }
            }, 3000)
          }
        }
        if (isMounted.current) {
          setLoading(false)
        }
      })
      .catch((err) => {
        if (isMounted.current) {
          setLoading(false)
          localStorage.removeItem(AUTH_USER_TOKEN_KEY)
          console.error(err)
          alert(err.message)
        }
      })
  }

  return (
    <Container>
      <LoginBox elevation={1}>
        <Flex direction="column">
          {newPasswordRequired
            ? (
              <>
                <SecondaryHeader>Password Reset Required</SecondaryHeader>
                <StyledInput
                  id="reset-password-1"
                  label="Password"
                  setWidth="100%"
                  type="password"
                  required={formTouched.password1}
                  requiredString="Password required."
                  setValue={(value: string) => {
                    setFormTouched({ ...formTouched, password1: true })
                    setFormData(() => ({ ...formData, password1: value }))
                  }}
                  value={formData.password1} />
                <StyledInput
                  id="reset-password-2"
                  label="Password"
                  setWidth="100%"
                  type="password"
                  required={formTouched.password2}
                  requiredString="Password required."
                  setValue={(value: string) => {
                    setFormTouched({ ...formTouched, password2: true })
                    setFormData(() => ({ ...formData, password2: value }))
                  }}
                  value={formData.password2} />
                <Button disabled={!isPasswordResetValid || isLoading} onClick={() => resetPassword()} variant="contained" color="primary">
                  Reset Password
                </Button>
              </>
            )
            : (
              <>
                <SecondaryHeader>Welcome to Epidesigner</SecondaryHeader>
                <StyledInput
                  id="login-email"
                  label="E-Mail"
                  setWidth="100%"
                  type="email"
                  required={formTouched.username}
                  requiredString="E-Mail required."
                  setValue={(value: string) => {
                    setFormTouched({ ...formTouched, username: true })
                    setFormData(() => ({ ...formData, username: value }))
                  }}
                  value={formData.username} />
                <StyledInput
                  id="login-password"
                  label="Password"
                  setWidth="100%"
                  type="password"
                  required={formTouched.password}
                  requiredString="Password required."
                  setValue={(value: string) => {
                    setFormTouched({ ...formTouched, password: true })
                    setFormData(() => ({ ...formData, password: value }))
                  }}
                  value={formData.password} />
                <Button disabled={!isLoginValid || isLoading} onClick={() => sendCredentials()} variant="contained" color="primary">
                  Sign In
                </Button>
                <StyledLink to="/forgot-password">
                  I forgot my password
                </StyledLink>
              </>
            )}
        </Flex>
      </LoginBox>
    </Container>
  )
}

const StyledInput = styled(Input)`
  margin-bottom: 24px !important;
  
  .MuiOutlinedInput-root {
    border-radius: 10px;
    background: #f7fafc;
    transition: all 0.2s ease;
    
    &:hover {
      background: #edf2f7;
    }
    
    &.Mui-focused {
      background: white;
      box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
    }
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: #3f51b5;
  text-align: center;
  margin-top: 24px !important;
  font-weight: 600;
  font-size: 14px;
  transition: color 0.2s ease;
  
  &:hover {
    color: #303f9f;
    text-decoration: underline;
  }
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 50%, #1a202c 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(63, 81, 181, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(63, 81, 181, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`

const LoginBox = styled(Paper)`
  padding: 48px;
  display: flex;
  justify-content: center;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  background: white;
  min-width: 400px;
  position: relative;
  z-index: 1;
  
  ${Flex} {
    width: 100%;
    
    ${StyledInput} {
      margin-bottom: 20px;
    }
    
    ${SecondaryHeader} {
      margin-bottom: 36px;
      text-align: center;
      font-size: 28px;
      font-weight: 800;
      color: #1a202c;
      letter-spacing: -0.5px;
    }
  }
  
  .MuiButton-containedPrimary {
    background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
    border-radius: 10px;
    padding: 14px 24px;
    font-weight: 700;
    text-transform: none;
    font-size: 16px;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 14px rgba(63, 81, 181, 0.4);
    transition: all 0.2s ease;
    
    &:hover {
      background: linear-gradient(135deg, #303f9f 0%, #3f51b5 100%);
      box-shadow: 0 6px 20px rgba(63, 81, 181, 0.5);
      transform: translateY(-1px);
    }
    
    &:disabled {
      background: #e2e8f0;
      box-shadow: none;
      color: #a0aec0;
    }
  }
`
